import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    MessageCircle,
    Phone,
    Play,
    X,
    Youtube,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LedTicker } from "../components/LedTicker";
import { SITE } from "../data/site";
import { PRODUCTS, getProduct } from "../data/products";

const ACCENTS = {
    amber: { text: "text-[#FF9900]", glow: "led-glow-amber", border: "hover:border-[#FF9900]/40", dot: "bg-[#FF9900]" },
    blue: { text: "text-[#00A8FF]", glow: "led-glow-blue", border: "hover:border-[#00A8FF]/40", dot: "bg-[#00A8FF]" },
    red: { text: "text-[#FF2D2D]", glow: "led-glow-red", border: "hover:border-[#FF2D2D]/40", dot: "bg-[#FF2D2D]" },
};

const Breadcrumb = ({ product }) => (
    <nav aria-label="Breadcrumb" className="text-sm text-slate-500" data-testid="breadcrumb">
        <ol className="flex flex-wrap items-center gap-2">
            <li>
                <Link to="/" data-testid="breadcrumb-home" className="transition-colors hover:text-[#FF9900]">Home</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>Products</li>
            <li aria-hidden="true">/</li>
            <li className="text-slate-300" aria-current="page">{product.short}</li>
        </ol>
    </nav>
);

const FaqItem = ({ faq, testid }) => (
    <details
        data-testid={testid}
        className="group rounded-xl border border-slate-800 bg-[#12141E] px-5 py-4 open:border-[#FF9900]/40"
    >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-slate-100 [&::-webkit-details-marker]:hidden">
            {faq.q}
            <ChevronDown className="h-5 w-5 shrink-0 text-slate-500 transition-transform group-open:rotate-180" aria-hidden="true" />
        </summary>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{faq.a}</p>
    </details>
);

const SECTIONS = [
    ["overview", "Overview"],
    ["specifications", "Specifications"],
    ["features", "Features"],
    ["photo-album", "Photos"],
    ["video-clips", "Videos"],
    ["faq", "FAQ"],
    ["enquiry", "Enquire"],
];

const SectionNav = () => (
    <nav
        aria-label="Sections on this page"
        data-testid="section-nav"
        className="sticky top-16 z-30 border-b border-slate-800/70 bg-[#0A0B10]/95"
    >
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 py-2.5 sm:px-6">
            {SECTIONS.map(([id, label]) => (
                <a
                    key={id}
                    href={`#${id}`}
                    data-testid={`section-link-${id}`}
                    className="whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 transition-colors hover:bg-[#1A1D2B] hover:text-[#FF9900]"
                >
                    {label}
                </a>
            ))}
        </div>
    </nav>
);

const Lightbox = ({ item, onClose }) => (
    <AnimatePresence>
        {item && (
            <motion.div
                data-testid="gallery-lightbox"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-6"
                onClick={onClose}
            >
                <button
                    data-testid="lightbox-close"
                    aria-label="Close image viewer"
                    className="absolute right-6 top-6 rounded-full border border-slate-600 p-2 text-slate-200 hover:border-[#FF9900]"
                    onClick={onClose}
                >
                    <X className="h-5 w-5" />
                </button>
                <figure className="max-w-4xl">
                    <motion.img
                        initial={{ scale: 0.94 }}
                        animate={{ scale: 1 }}
                        src={item.src}
                        alt={item.alt}
                        className="max-h-[78vh] w-auto rounded-xl border border-slate-700 object-contain"
                    />
                    <figcaption className="mt-3 text-center text-sm text-slate-400">{item.caption}</figcaption>
                </figure>
            </motion.div>
        )}
    </AnimatePresence>
);

export default function ProductPage() {
    const { slug } = useParams();
    const product = getProduct(slug);
    const [lightbox, setLightbox] = useState(null);

    if (!product) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="product-not-found">
                <h1 className="font-display text-4xl font-bold uppercase">Product not found</h1>
                <p className="mt-4 text-slate-400">The product page you asked for does not exist.</p>
                <Link to="/" data-testid="notfound-home-link" className="mt-6 inline-flex items-center gap-2 font-semibold text-[#FF9900]">
                    Back to home <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        );
    }

    const a = ACCENTS[product.accent] || ACCENTS.amber;
    const path = `/products/${product.slug}`;
    const url = `${SITE.domain}${path}`;
    const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3);

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.metaDescription,
            image: product.heroImage,
            url,
            category: product.category,
            brand: { "@type": "Brand", name: "Morningstar Enterprises" },
            manufacturer: {
                "@type": "Organization",
                name: "Morning Star Enterprises",
                url: SITE.domain + "/",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain + "/" },
                { "@type": "ListItem", position: 2, name: product.name, item: url },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: product.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    ];

    return (
        <>
            <Seo
                title={product.metaTitle}
                description={product.metaDescription}
                keywords={product.keywords}
                path={path}
                jsonLd={jsonLd}
            />

            {/* Hero */}
            <section className="led-dots spotlight relative overflow-hidden border-b border-slate-800/70" data-testid="product-hero">
                <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20">
                    <div>
                        <Breadcrumb product={product} />
                        <p className={`mt-6 font-led text-lg uppercase tracking-[0.35em] ${a.text}`}>
                            {product.category}
                        </p>
                        <div className="mt-3 overflow-hidden">
                            <motion.h1
                                initial={{ y: "105%" }}
                                animate={{ y: "0%" }}
                                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
                            >
                                {product.name}
                            </motion.h1>
                        </div>
                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35, duration: 0.7 }}
                            className="mt-5 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
                        >
                            {product.tagline}
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.7 }}
                            className="mt-6 flex flex-wrap gap-2"
                        >
                            {product.pills.map((pill) => (
                                <span
                                    key={pill}
                                    className="rounded-full border border-slate-700 bg-[#12141E] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-300"
                                >
                                    {pill}
                                </span>
                            ))}
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.62, duration: 0.7 }}
                            className="mt-8 flex flex-wrap gap-4"
                        >
                            <a
                                href={SITE.whatsappFor(product.name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="product-whatsapp-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                            >
                                <MessageCircle className="h-5 w-5" /> Get Technical &amp; Commercial Details
                            </a>
                            <a
                                href={SITE.phoneTel}
                                data-testid="product-call-cta"
                                className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 font-semibold text-slate-200 transition-colors hover:border-[#FF9900] hover:text-[#FF9900]"
                            >
                                <Phone className="h-5 w-5" /> {SITE.phoneDisplay}
                            </a>
                        </motion.div>
                    </div>
                    <motion.figure
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div className={`absolute -inset-6 rounded-full ${product.accent === "blue" ? "bg-[#00A8FF]/10" : product.accent === "red" ? "bg-[#FF2D2D]/10" : "bg-[#FF9900]/10"} blur-3xl`} aria-hidden="true" />
                        <img
                            src={product.heroImage}
                            alt={product.heroAlt}
                            className={`relative w-full rounded-2xl border border-slate-700 shadow-2xl shadow-black/60 [clip-path:polygon(0_0,100%_0,100%_92%,92%_100%,0_100%)] ${
                                product.heroFit === "contain"
                                    ? "max-h-[32rem] bg-white object-contain p-4"
                                    : "object-cover"
                            }`}
                        />
                    </motion.figure>
                </div>
                <LedTicker
                    accent={product.accent}
                    messages={[product.name, ...product.pills, `WhatsApp ${SITE.phoneDisplay}`]}
                />
            </section>
            <SectionNav />

            {/* Overview */}
            <section id="overview" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6" data-testid="product-overview">
                <div className="grid gap-10 lg:grid-cols-5">
                    <Reveal className="lg:col-span-3">
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            {product.overview.heading}
                        </h2>
                        {product.overview.paras.map((p, i) => (
                            <p key={i} className="mt-4 text-base leading-relaxed text-slate-400">
                                {p}
                            </p>
                        ))}
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-2">
                        <div className="rounded-2xl border border-slate-800 bg-[#12141E] p-6">
                            <h3 className="font-display text-xl font-semibold uppercase tracking-wide">
                                At a glance
                            </h3>
                            <ul className="mt-4 space-y-3">
                                {product.overview.points.map((pt) => (
                                    <li key={pt} className="flex gap-3 text-sm text-slate-300">
                                        <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${a.text}`} aria-hidden="true" />
                                        {pt}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.1}>
                    <h3 className="mt-12 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                        Where it works
                    </h3>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {product.applications.map((app) => (
                            <span
                                key={app}
                                className={`rounded-full border border-slate-800 bg-[#12141E] px-4 py-2 text-sm text-slate-300 transition-colors ${a.border}`}
                            >
                                {app}
                            </span>
                        ))}
                    </div>
                </Reveal>
                {product.alsoKnownAs && (
                    <Reveal delay={0.12}>
                        <h3
                            data-testid="also-known-as"
                            className="mt-10 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500"
                        >
                            People also search for this as
                        </h3>
                        <ul className="mt-4 flex flex-wrap gap-2">
                            {product.alsoKnownAs.map((term) => (
                                <li
                                    key={term}
                                    className="rounded-full bg-[#1A1D2B] px-3 py-1.5 text-xs text-slate-400"
                                >
                                    {term}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                )}
            </section>

            {/* Specs */}
            <section id="specifications" className="scroll-mt-28 border-y border-slate-800/70 bg-[#0C0E16]" data-testid="product-specs">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            Technical specifications
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-800">
                            <table className="w-full min-w-[36rem] text-left text-sm">
                                <tbody>
                                    {product.specs.map((s, i) => (
                                        <tr key={s.k} className={i % 2 ? "bg-[#12141E]" : "bg-[#0E1019]"}>
                                            <th scope="row" className="w-2/5 px-5 py-4 font-semibold text-slate-200">
                                                {s.k}
                                            </th>
                                            <td className="px-5 py-4 text-slate-400">{s.v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6" data-testid="product-features">
                <Reveal>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                        Why this one
                    </h2>
                </Reveal>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {product.features.map((f, i) => (
                        <Reveal key={f.title} delay={(i % 3) * 0.08}>
                            <article className={`h-full rounded-2xl border border-slate-800 bg-[#12141E] p-6 transition-colors ${a.border}`}>
                                <span className={`inline-block h-2 w-2 rounded-full ${a.dot}`} aria-hidden="true" />
                                <h3 className="mt-3 font-display text-xl font-semibold uppercase tracking-wide">
                                    {f.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Photo album */}
            <section id="photo-album" className="scroll-mt-28 border-y border-slate-800/70 bg-[#0C0E16]" data-testid="product-gallery">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            Photo album
                        </h2>
                        <p className="mt-2 text-sm text-slate-500">Tap any photo to view it full-screen.</p>
                    </Reveal>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {product.gallery.map((g, i) => (
                            <Reveal key={g.src + i} delay={(i % 4) * 0.06}>
                                <button
                                    data-testid={`gallery-item-${i}`}
                                    className="group block w-full overflow-hidden rounded-xl border border-slate-800 text-left"
                                    onClick={() => setLightbox(g)}
                                >
                                    <img
                                        src={g.src}
                                        alt={g.alt}
                                        loading="lazy"
                                        className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <span className="block bg-[#12141E] px-3 py-2 text-xs text-slate-400">
                                        {g.caption}
                                    </span>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Video clips album */}
            <section id="video-clips" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6" data-testid="product-videos">
                <Reveal>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                        Video clips album
                    </h2>
                </Reveal>
                {product.videos.length > 0 ? (
                    <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {product.videos.map((v, i) => (
                            <Reveal key={v.id} delay={i * 0.08}>
                                <figure className="overflow-hidden rounded-2xl border border-slate-800 bg-[#12141E]">
                                    <div className="aspect-video">
                                        {v.src ? (
                                            <video
                                                src={v.src}
                                                title={v.title}
                                                controls
                                                preload="metadata"
                                                className="h-full w-full bg-black"
                                                data-testid={`video-embed-${i}`}
                                            />
                                        ) : (
                                            <iframe
                                                src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                                                title={v.title}
                                                loading="lazy"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                className="h-full w-full"
                                                data-testid={`video-embed-${i}`}
                                            />
                                        )}
                                    </div>
                                    <figcaption className="px-4 py-3 text-sm text-slate-400">{v.title}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                ) : (
                    <Reveal delay={0.1}>
                        <div className="mt-8 flex flex-col items-start gap-5 rounded-2xl border border-slate-800 bg-[#12141E] p-8 sm:flex-row sm:items-center">
                            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#FF2D2D]/15">
                                <Play className="h-6 w-6 text-[#FF2D2D]" aria-hidden="true" />
                            </span>
                            <div className="flex-1">
                                <h3 className="font-display text-xl font-semibold uppercase tracking-wide">
                                    Demo clips for this product
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-slate-400">
                                    We record live demos of every installation. Ask on WhatsApp for
                                    current {product.short.toLowerCase()} clips, or browse our channel.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <a
                                    href={SITE.whatsappFor(product.name + " demo videos")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="videos-whatsapp-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-105"
                                >
                                    <MessageCircle className="h-4 w-4" /> Request Clips
                                </a>
                                <a
                                    href={SITE.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="videos-youtube-link"
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
                                >
                                    <Youtube className="h-4 w-4" /> YouTube Channel
                                </a>
                            </div>
                        </div>
                    </Reveal>
                )}
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-28 border-y border-slate-800/70 bg-[#0C0E16]" data-testid="product-faq">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            Frequently asked questions
                        </h2>
                    </Reveal>
                    <div className="mt-8 space-y-3">
                        {product.faqs.map((f, i) => (
                            <Reveal key={f.q} delay={i * 0.05}>
                                <FaqItem faq={f} testid={`faq-item-${i}`} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA + related */}
            <section id="enquiry" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-16 sm:px-6" data-testid="product-cta">
                <Reveal>
                    <div className="led-dots rounded-3xl border border-slate-800 bg-[#12141E] p-10 text-center">
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            Planning a {product.short.toLowerCase()}?{" "}
                            <span className={a.glow}>Talk to us first.</span>
                        </h2>
                        <p className="mx-auto mt-3 max-w-xl text-base text-slate-400">
                            Send your site photo and dimensions on WhatsApp — we reply with the
                            right specification and a budget for your location in Kerala, Tamil
                            Nadu or Karnataka.
                        </p>
                        <a
                            href={SITE.whatsappFor(product.name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="product-bottom-whatsapp"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                        >
                            <MessageCircle className="h-5 w-5" /> Chat on WhatsApp — {SITE.phoneDisplay}
                        </a>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-14 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                        Also explore
                    </h2>
                    <div className="mt-4 grid gap-4 sm:grid-cols-3">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/products/${r.slug}`}
                                data-testid={`related-${r.slug}`}
                                className="group rounded-xl border border-slate-800 bg-[#12141E] p-5 transition-colors hover:border-[#FF9900]/40"
                            >
                                <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-slate-100">
                                    {r.name}
                                </h3>
                                <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#FF9900]">
                                    View <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </Reveal>
            </section>

            <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
        </>
    );
}
